import { useCallback } from 'react';

import { Stack } from '../../../../../components/Stack';
import { useStepWizard } from '../../../../../components/StepWizard';
import { DATADOG_ACTIONS } from '../../../constants';
import { trackSdkCall } from '../../../helpers/trackSdkCall';
import { useDataDogActions } from '../../../hooks/useDataDogActions';
import { PageProps } from '../types';
import { Button } from './shared/Button';

interface ActionSectionProps extends PageProps {
  context?: 'default' | 'success' | 'error' | 'loading';
  isPortalPreview: boolean;
  confirm?: string;
  editAddress?: string;
  close?: string;
  done?: string;
  isLoading: boolean;
  status?: string;
  handleSubmit: () => void;
  errorMessage?: string;
  // True for error_code EMERGENCY_ADDRESS_VALIDATION_FAILED (the ES remaps the
  // carrier 422 to HTTP 400 + this code) — unlocks the "Edit Address"
  // recovery path. Decided upstream in Confirmation.
  isAddressError?: boolean;
  // New (autocomplete) flow: two-button layouts per Figma 2454-845 — no
  // Close on the default screen, no Cancel on the address-error screen.
  autocompleteEnabled?: boolean;
  deviceSdk?: any;
  defaultTexts: {
    confirm: string;
    editAddress: string;
    close: string;
    done: string;
    cancel: string;
  };
}

export const ActionSection = ({
  context = 'default',
  confirm,
  editAddress,
  close,
  done,
  isLoading,
  isAddressError = false,
  autocompleteEnabled = false,
  handleSubmit,
  deviceSdk,
  defaultTexts,
  isPortalPreview,
}: ActionSectionProps) => {
  const { prevStep } = useStepWizard();
  const { dataDogLogger, actions } = useDataDogActions({
    isPortalPreview,
    actionKey: 'vowifi',
  });

  const handleCancel = useCallback(() => {
    if (isPortalPreview) {
      return;
    }

    if (deviceSdk && deviceSdk.voWifiActivationCancel) {
      // cancel emits its own teardown action; stop the session after so that
      // log is captured in it.
      trackSdkCall(
        'voWifiActivationCancel',
        () => deviceSdk.voWifiActivationCancel(),
        dataDogLogger,
      );
      dataDogLogger.stopSession();
      return;
    }

    // The SDK method is missing — cancel does nothing. Report the silent
    // failure, then close the session.
    dataDogLogger.error(DATADOG_ACTIONS.errors.template_error, {
      flow: 'sdk_unavailable',
      errorCode: 'SDK_METHOD_MISSING',
      errorMessage: 'deviceSdk.voWifiActivationCancel is unavailable',
      method: 'voWifiActivationCancel',
    });
    dataDogLogger.stopSession();
  }, [deviceSdk, dataDogLogger, isPortalPreview]);

  const handlePrevStep = useCallback(() => {
    if (isPortalPreview) {
      return;
    }

    prevStep();
  }, [prevStep, dataDogLogger, isPortalPreview]);

  // Success state - show Done button
  if (context === 'success') {
    return (
      <Stack gap={3} direction="column">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          data-testid="vowifi-done-button"
          data-dd-action-name={actions?.confirmation_done_button_clicked}
        >
          {done ? done : defaultTexts.done}
        </Button>
      </Stack>
    );
  }

  // Default state - show Confirm, Edit Address, and Close buttons
  if (context !== 'error') {
    return (
      <Stack gap={3} direction="column">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          data-testid="vowifi-confirm-button"
          data-dd-action-name={actions?.confirmation_confirm_button_clicked}
        >
          {confirm ? confirm : defaultTexts.confirm}
        </Button>
        <Button
          type="button"
          onClick={handlePrevStep}
          disabled={isLoading}
          ghost
          data-testid="vowifi-edit-address-button"
          data-dd-action-name={
            actions?.confirmation_edit_address_button_clicked
          }
        >
          {editAddress ? editAddress : defaultTexts.editAddress}
        </Button>
        {!autocompleteEnabled && (
          <Button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            ghost
            data-testid="vowifi-close-button"
            data-dd-action-name={actions?.confirmation_close_button_clicked}
          >
            {close ? close : defaultTexts.close}
          </Button>
        )}
      </Stack>
    );
  }

  // Error state with address validation error - show disabled Confirm, Edit Address, and Cancel
  if (isAddressError) {
    return (
      <Stack gap={3} direction="column">
        <Button
          type="button"
          onClick={handleSubmit}
          disabled
          data-testid="vowifi-confirm-button"
          data-dd-action-name={actions?.confirmation_confirm_button_clicked}
        >
          {confirm ? confirm : defaultTexts.confirm}
        </Button>

        <Button
          type="button"
          onClick={handlePrevStep}
          ghost
          data-testid="vowifi-edit-address-button"
          data-dd-action-name={
            actions?.confirmation_edit_address_button_clicked
          }
        >
          {editAddress ? editAddress : defaultTexts.editAddress}
        </Button>

        {!autocompleteEnabled && (
          <Button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            ghost
            data-testid="vowifi-cancel-button"
            data-dd-action-name={actions?.confirmation_cancel_button_clicked}
          >
            {close ? close : defaultTexts.close}
          </Button>
        )}
      </Stack>
    );
  }

  // Error state (other errors) - show only Close button
  return (
    <Stack gap={3} direction="column">
      <Button
        type="button"
        onClick={handleCancel}
        disabled={isLoading}
        data-dd-action-name={actions?.confirmation_close_button_clicked}
      >
        {close ? close : defaultTexts.close}
      </Button>
    </Stack>
  );
};
